import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';

import useSnackbarHelper from '../../../hooks/snackbar.hook';
import useService from '../../../hooks/service.hook';
import TrainingCommentsService from '../../../services/training-comments.service';
import { TrainingComment } from '../../../models/training-comment.models';
import CommentCard, {
  getEffortTierBackgroundColor,
  getEffortTierTextColor,
} from '../../coe-training-modal-v2/tabs/comment-card';
import { palette } from '../../../theme';

export interface CoachAthleteCommentsTabProps {
  trainingId: string;
  memberId: string | number;
  effortRating?: number | null;
  onUnreadCommentsCleared?: () => void;
}

const CoachAthleteCommentsTab: FC<CoachAthleteCommentsTabProps> = ({
  trainingId,
  memberId,
  effortRating,
  onUnreadCommentsCleared,
}) => {
  const trainingCommentsService = useService(TrainingCommentsService);
  const { showSnackbar } = useSnackbarHelper();

  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [comments, setComments] = useState<TrainingComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<TrainingComment | null>(null);
  const [deletingComment, setDeletingComment] = useState(false);

  const [commentMenuAnchorEl, setCommentMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [commentMenuId, setCommentMenuId] = useState<number | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentMessage, setEditingCommentMessage] = useState('');

  const fetchComments = () => {
    if (!trainingId) return;

    setCommentsLoading(true);
    setCommentsError('');

    trainingCommentsService
      .getTrainingComments(trainingId, memberId)
      .then((response) => {
        setComments(response.data.data || []);
        setCommentsLoading(false);
      })
      .catch(() => {
        setCommentsLoading(false);
        setCommentsError('Failed to load comments');
        showSnackbar('There was an error loading comments. Please try again later.', 'error');
      });
  };

  const markCommentsAsRead = () => {
    if (!trainingId) return;

    trainingCommentsService
      .markCommentsAsRead(trainingId, memberId)
      .then(() => {
        onUnreadCommentsCleared?.();
      })
      .catch(() => {
        void 0;
      });
  };

  useEffect(() => {
    if (trainingId && memberId) {
      fetchComments();
      markCommentsAsRead();
    }
  }, [trainingId, memberId]);

  const handleRefresh = () => {
    fetchComments();
    markCommentsAsRead();
  };

  const handleCommentSubmit = () => {
    if (!trainingId) return;

    const trimmedText = commentText.trim();
    if (!trimmedText) {
      setCommentError('Comment cannot be empty');
      return;
    }

    setCommentSubmitting(true);
    setCommentError('');

    trainingCommentsService
      .createTrainingComment(trainingId, trimmedText, memberId)
      .then(() => {
        setCommentSubmitting(false);
        setCommentText('');
        showSnackbar('Comment added successfully', 'success');
        fetchComments();
      })
      .catch(() => {
        setCommentSubmitting(false);
        showSnackbar('There was an error adding your comment. Please try again later.', 'error');
      });
  };

  const handleCommentTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentText(event.target.value);
    if (commentError) setCommentError('');
  };

  const handleCommentMenuOpen = (event: React.MouseEvent<HTMLElement>, commentId: number) => {
    event.stopPropagation();
    setCommentMenuAnchorEl(event.currentTarget);
    setCommentMenuId(commentId);
  };

  const handleCommentMenuClose = () => {
    setCommentMenuAnchorEl(null);
    setCommentMenuId(null);
  };

  const handleEditComment = (commentId: number) => {
    handleCommentMenuClose();
    const comment = comments.find((c) => c.id === commentId);
    if (comment) {
      setEditingCommentId(commentId);
      setEditingCommentMessage(comment.message);
    }
  };

  const handleCancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditingCommentMessage('');
  };

  const handleSaveComment = async (commentId: number) => {
    if (!trainingId) return;
    const trimmedMessage = editingCommentMessage.trim();
    const comment = comments.find((c) => c.id === commentId);
    if (!comment || trimmedMessage === comment.message.trim()) {
      handleCancelCommentEdit();
      return;
    }

    try {
      const res = await trainingCommentsService.updateTrainingComment(trainingId, commentId, trimmedMessage);
      const updatedAt = res.data.data?.updated_at || new Date().toISOString();
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, message: trimmedMessage, updated_at: updatedAt } : c))
      );
      showSnackbar('Comment updated', 'success');
      handleCancelCommentEdit();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showSnackbar(err?.response?.data?.message || 'Failed to update comment. Please try again.', 'error');
    }
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLElement).blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelCommentEdit();
    }
  };

  const handleDeleteComment = (comment: TrainingComment) => {
    handleCommentMenuClose();
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteComment = () => {
    if (!commentToDelete || !trainingId) return;

    setDeletingComment(true);
    trainingCommentsService
      .deleteTrainingComment(trainingId, commentToDelete.id)
      .then(() => {
        setDeletingComment(false);
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
        showSnackbar('Comment deleted successfully', 'success');
        fetchComments();
      })
      .catch(() => {
        setDeletingComment(false);
        showSnackbar('There was an error deleting your comment. Please try again later.', 'error');
      });
  };

  const cancelDeleteComment = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const sharedCardProps = {
    commentMenuAnchorEl,
    commentMenuId,
    onCommentMenuOpen: handleCommentMenuOpen,
    onCommentMenuClose: handleCommentMenuClose,
    onEditComment: handleEditComment,
    onDeleteComment: handleDeleteComment,
    editingCommentId,
    editingCommentMessage,
    onEditingMessageChange: setEditingCommentMessage,
    onSaveComment: handleSaveComment,
    onCommentKeyDown: handleCommentKeyDown,
  };

  const showEffortRating = effortRating != null && effortRating > 0;
  const effortRatingValue = effortRating ?? 0;

  return (
    <Box sx={{ padding: '16px', minHeight: 'fit-content', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          onClick={handleRefresh}
          disabled={commentsLoading}
          size="small"
          sx={{
            backgroundColor: palette.grey[100],
            '&:hover': { backgroundColor: palette.grey[200] },
          }}
          aria-label="Refresh comments"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {showEffortRating && (
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 1.5,
              fontSize: 11,
              fontWeight: 600,
              color: palette.text.secondary,
              mb: 1.5,
            }}
          >
            Perceived Effort
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 36,
              height: 36,
              borderRadius: 1,
              fontWeight: 700,
              fontSize: 13,
              px: 1.5,
              backgroundColor: getEffortTierBackgroundColor(effortRatingValue),
              color: getEffortTierTextColor(effortRatingValue),
              border: `1px solid ${getEffortTierBackgroundColor(effortRatingValue)}`,
            }}
          >
            {effortRatingValue}
          </Box>
          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {commentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
            <CircularProgress size={30} />
          </Box>
        ) : commentsError ? (
          <Typography variant="body1" color="error">
            {commentsError}
          </Typography>
        ) : comments.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 36, color: palette.grey[300] }} />
            <Typography variant="body2" color={palette.text.disabled}>
              No notes yet. Be the first to add one!
            </Typography>
          </Box>
        ) : (
          comments.map((comment) => (
            <CommentCard
              key={comment.id}
              variant="parent"
              comment={comment}
              canShowEditMenu={!!comment.can_edit}
              {...sharedCardProps}
            />
          ))
        )}
      </Box>

      <Box sx={{ borderTop: `1px solid ${palette.divider}`, pt: 2, mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: palette.text.secondary, mb: 1.5 }}>
          Add a Comment
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={commentText}
          onChange={handleCommentTextChange}
          placeholder="Write your comment here..."
          variant="outlined"
          error={!!commentError}
          helperText={commentError}
          disabled={commentSubmitting}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              fontSize: 14,
              borderRadius: 2,
              backgroundColor: palette.background.default,
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCommentSubmit}
            disabled={commentSubmitting || !commentText.trim()}
            sx={{ textTransform: 'none', minWidth: 100, borderRadius: 2 }}
          >
            {commentSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Post Comment'}
          </Button>
        </Box>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={cancelDeleteComment}>
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this comment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteComment} disabled={deletingComment}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteComment}
            color="error"
            variant="contained"
            disabled={deletingComment}
            startIcon={deletingComment ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deletingComment ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoachAthleteCommentsTab;
